import { searchTranslationMemory } from "../api/translationMemoryApi";
import { useState, useCallback, useRef, useEffect } from "react";
import { TranslationMemory } from "./types";

type UseTranslationMemoriesReturn = {
  items: { [key: number]: TranslationMemory[] };
  search: (
    text: string,
    sourceLanguage: string,
    targetLanguage: string,
    index: number,
    byField?: "source_text" | "target_text",
    skipIds?: number[]
  ) => Promise<void>;
  getAtIndex: (index: number) => TranslationMemory[];
  isSearching: boolean;
  clear: () => void;
};

/**
 * A hook that manages a list of translation memories.
 * @returns An object containing the translation memories, and functions to search for them.
 */
export const useTranslationMemories = (): UseTranslationMemoriesReturn => {
  const [items, setItems] = useState<{ [key: number]: TranslationMemory[] }>(
    {}
  );
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const search = useCallback(
    async (
      text: string,
      sourceLanguage: string | null,
      targetLanguage: string,
      index: number,
      byField: "source_text" | "target_text" = "source_text",
      skipIds: number[] = []
    ): Promise<void> => {
      if (!targetLanguage) {
        return;
      }
      setIsSearching(true);
      const internalSkipIds = items[index]?.map((tm) => tm.id) || [];
      const translationMemory = await searchTranslationMemory(
        text,
        sourceLanguage,
        targetLanguage,
        byField,
        byField === "source_text" ? [...internalSkipIds, ...skipIds] : []
      );

      if (!mountedRef.current) return;
      let newItems = translationMemory;
      if (byField === "source_text") {
        const oldItems = items[index] || [];
        newItems = [...oldItems, ...translationMemory];
      }
      setItems((prev) => ({
        ...prev,
        [index]: newItems.sort((a, b) => b.similarity - a.similarity),
      }));

      if (!mountedRef.current) return;
      setIsSearching(false);
    },
    [items]
  );

  const getAtIndex = useCallback(
    (index: number): TranslationMemory[] => {
      return items[index] || [];
    },
    [items]
  );

  const clear = useCallback(() => {
    setItems({});
  }, [setItems, items]);

  return {
    items,
    search,
    getAtIndex,
    isSearching,
    clear,
  };
};
